import type { Boss, Player } from './types'
import { arenaBoundsFor, FIXED_DT } from './balance'

// 2026-09-15: ユーザー承認の試行のみ。URLはgame.tsのDEVゲートで受け付ける。
export type BossAttackTravelMode = 'legacy' | 'settle' | 'planted'
let mode: BossAttackTravelMode = 'legacy'
export function setBossAttackTravelMode(value: BossAttackTravelMode): void { mode = value }
export function getBossAttackTravelMode(): BossAttackTravelMode { return mode }

type SettleProfile = {
  recoveryHold: number; returnFraction: number; maxReturnPx: number; speed: number
  gap?: { hold: number; returnFraction: number; maxReturnPx: number }
}
// 初回の比較対象だけ。全技共通の距離ではなく、同じ移動の管理に技ごとの配分を持たせる。
const PROFILES: Record<string, SettleProfile> = {
  feralBite: { recoveryHold: 0.60, returnFraction: 0.65, maxReturnPx: 42, speed: 160 },
  feralFrenzy: { recoveryHold: 0.60, returnFraction: 0.65, maxReturnPx: 60, speed: 160,
    gap: { hold: 0.35, returnFraction: 0.60, maxReturnPx: 20 } },
  lanceThrust: { recoveryHold: 0.65, returnFraction: 0.65, maxReturnPx: 45, speed: 160 },
}
export type BossAttackTravel = {
  attackId: string; originX: number; segmentX: number; dir: 1 | -1
  phase: Boss['state']; amount: number; returned: number; cancelled: boolean
}
function profile(boss: Boss): SettleProfile | undefined {
  const a = boss.currentAttack
  if (mode !== 'settle' || !a || a.passThroughBody || a.positionEvadable || a.crossArena
      || a.playerLethal || a.bossFinisher || a.comboLunge || a.recoveryDriftPx || a.recoveryRecenter) return
  return PROFILES[a.id]
}

// 新しい技・中断で持ち越さない。active開始位置と連撃全体の開始位置を区別する。
export function prepareBossAttackTravel(boss: Boss): Boss {
  const p = profile(boss)
  if (!p || !['active', 'comboGap', 'recovery', 'beatGap'].includes(boss.state)) {
    if (!boss.attackTravel) return boss
    const { attackTravel: _old, ...rest } = boss
    return rest
  }
  let travel = boss.attackTravel
  if (!travel || travel.attackId !== boss.currentAttack!.id) {
    if (boss.state !== 'active') return boss
    travel = { attackId: boss.currentAttack!.id, originX: boss.actionAnchorX,
      segmentX: boss.actionAnchorX, dir: boss.facing === 'right' ? 1 : -1,
      phase: 'active', amount: 0, returned: 0, cancelled: false }
  } else if (travel.phase !== boss.state) {
    if (boss.state === 'active') {
      travel = { ...travel, phase: 'active', segmentX: boss.actionAnchorX, amount: 0, returned: 0, cancelled: false }
    } else {
      const gap = boss.state === 'comboGap'
      const part = gap ? p.gap : p
      const advance = Math.max(0, (boss.x - (gap ? travel.segmentX : travel.originX)) * travel.dir)
      travel = { ...travel, phase: boss.state,
        amount: part ? Math.min(part.maxReturnPx, advance * part.returnFraction) : 0,
        returned: 0, cancelled: false }
    }
  }
  return { ...boss, attackTravel: travel }
}

// activeの前進・速度・当たり判定には触れない。隙の後半で実際に進んだ分の一部だけ収める。
export function settleBossAttackTravel(boss: Boss, player: Player, durationMs: number): Boss {
  const p = profile(boss), t = boss.attackTravel
  if (!p || !t || t.cancelled || t.amount <= 0 || durationMs <= 0) return boss
  const hold = boss.state === 'comboGap' ? p.gap?.hold : p.recoveryHold
  if (hold == null || boss.state === 'active') return boss
  // 回り込んだ相手へ後退して重ならない。相手が戻っても、この相の戻りは再開しない。
  if ((player.x - boss.x) * t.dir <= 0) return { ...boss, attackTravel: { ...t, cancelled: true } }
  const progress = Math.max(0, Math.min(1, (boss.stateElapsed * 1000 / durationMs - hold) / (1 - hold)))
  const eased = progress * progress * (3 - 2 * progress)
  const step = Math.min(p.speed * FIXED_DT, Math.max(0, t.amount * eased - t.returned))
  if (step === 0) return boss
  const walls = arenaBoundsFor(boss.id)
  const x = Math.max(walls.leftWall, Math.min(walls.rightWall, boss.x - t.dir * step))
  return { ...boss, x, attackTravel: { ...t, returned: t.returned + Math.abs(x - boss.x) } }
}
